// authSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface MyObject {
    [key: string]: any;
}

interface Invite {
    id: number;
    entityId: string;
    [key: string]: any;
}

interface EntityAccountState {
    email: string;
    businessName: string;
    password: string;
    userID: string;
    isEmailVerified: boolean;
    entityAccountUserDetails: MyObject;
    entityAccountBusinessDetails: MyObject;
    businessAddressDetails: MyObject;
    physicalBusinessAddressDetails: MyObject;
    accountUseDetails: MyObject;
    authorizedRepresentativeDetails: MyObject;
    beneficialOwnerDetails: MyObject;
    directorsDetails: MyObject;
    uploadDocumentDetails: MyObject;
    certificationDetails: MyObject;
    pageName: string;
    wizardPageNumber: number;
    checkPage: string;
    wizardStepValidation: {
        [key: number]: boolean;
    };
    invites: Invite[];
    kycToken: string;

}



const initialState: EntityAccountState = {
    email: '',
    businessName: '',
    password: '',
    userID: '',
    isEmailVerified: false,
    entityAccountUserDetails: {},
    entityAccountBusinessDetails: {},
    businessAddressDetails: {},
    physicalBusinessAddressDetails: {},
    accountUseDetails: {},
    authorizedRepresentativeDetails: {},
    beneficialOwnerDetails: {},
    directorsDetails: {},
    uploadDocumentDetails: {},
    certificationDetails: {},
    pageName: '',
    wizardPageNumber: 1,
    checkPage: '',
    wizardStepValidation: {
        1: true,
        2: true,
        3: true,
        4: true,
        5: true,
        6: true,
        7: true,
        8: true,
        9: true,
        10: true,
        11: true,
        12: true,
        13: true,
    },
    invites: [],
    kycToken: '',

};

// Create your auth slice
const EntityAccountReducer = createSlice({
    name: 'entityAccount',
    initialState,
    reducers: {
        setEmail: (state, action: PayloadAction<string>) => {
            state.email = action.payload;
        },
        setBusinessName: (state, action: PayloadAction<string>) => {
            state.businessName = action.payload;
        },
        setPassword: (state, action: PayloadAction<string>) => {
            state.password = action.payload;
        },
        setUserID: (state, action: PayloadAction<string>) => {
            state.userID = action.payload;
        },
        setEmailVerify: (state, action: PayloadAction<boolean>) => {
            state.isEmailVerified = action.payload;
        },

        setEntityAccountUserDetails: (
            state,
            action: PayloadAction<{ [key: string]: any }>
        ) => {
            state.entityAccountUserDetails = { ...action.payload };
        },

        setEntityAccountBusinessDetails: (
            state,
            action: PayloadAction<{ [key: string]: any }>
        ) => {
            state.entityAccountBusinessDetails = { ...action.payload };
        },
        setBusinessAddressDetails: (
            state,
            action: PayloadAction<{ [key: string]: any }>
        ) => {
            state.businessAddressDetails = { ...action.payload };
        },
        setPhysicalBusinessAddressDetails: (
            state,
            action: PayloadAction<{ [key: string]: any }>
        ) => {
            state.physicalBusinessAddressDetails = { ...action.payload };
        },
        setAccountUseDetails: (
            state,
            action: PayloadAction<{ [key: string]: any }>
        ) => {
            state.accountUseDetails = { ...action.payload };
        },
        setAuthorizedRepresentativeDetails: (
            state,
            action: PayloadAction<{ [key: string]: any }>
        ) => {
            state.authorizedRepresentativeDetails = { ...action.payload };
        },
        setBeneficialOwnerDetails: (
            state,
            action: PayloadAction<{ [key: string]: any }>
        ) => {
            state.beneficialOwnerDetails = { ...action.payload };
        },
        setDirectorsDetails: (
            state,
            action: PayloadAction<{ [key: string]: any }>
        ) => {
            state.directorsDetails = { ...action.payload };
        },
        setUploadDocumentDetails: (
            state,
            action: PayloadAction<{ [key: string]: any }>
        ) => {
            state.uploadDocumentDetails = { ...action.payload };
        },
        setCertificationDetails: (state, action: PayloadAction<MyObject>) => {
            state.certificationDetails = action.payload;
        },
        setwizardPageName: (state, action: PayloadAction<string>) => {
            state.pageName = action.payload;
        },
        setwizardPageNumber: (state, action: PayloadAction<number>) => {
            state.wizardPageNumber = action.payload;
        },
        setWizardStepValidation: (
            state,
            action: PayloadAction<{ step: number; isValid: boolean }>
        ) => {
            const { step, isValid } = action.payload;
            state.wizardStepValidation[step] = isValid;
        },
        setCheckPage: (state, action: PayloadAction<string>) => {
            state.checkPage = action.payload;
        },
        resetWizard: (state) => {
            state.wizardPageNumber = 1;
            state.wizardStepValidation = {
                1: true,
                2: true,
                3: true,
                4: true,
                5: true,
                6: true,
                7: true,
                8: true,
                9: true,
                10: true,
                11: true,
                12: true,
                13: true,
            };
        },
        setInvites: (state, action: PayloadAction<Invite[]>) => {
            state.invites = action.payload;
        },
        addInvite: (state, action: PayloadAction<Invite>) => {
            state.invites.push(action.payload);
        },
        updateInvite: (state, action: PayloadAction<{ id: number; invite: Invite }>) => {
            const { id, invite } = action.payload;
            const index = state.invites.findIndex(inv => inv.id === id);
            if (index !== -1) {
                state.invites[index] = invite;
            }
        },
        removeInvite: (state, action: PayloadAction<number>) => {
            state.invites = state.invites.filter(inv => inv.id !== action.payload);
        },
        clearInvites: (state) => {
            state.invites = [];
        },
        setKycToken: (state, action: PayloadAction<string>) => {
            state.kycToken = action.payload;
        },

    },
});

export const {
    setEmail,
    setBusinessName,
    setPassword,
    setEmailVerify,
    setUserID,
    setEntityAccountUserDetails,
    setEntityAccountBusinessDetails,
    setBusinessAddressDetails,
    setPhysicalBusinessAddressDetails,
    setAccountUseDetails,
    setAuthorizedRepresentativeDetails,
    setBeneficialOwnerDetails,
    setDirectorsDetails,
    setUploadDocumentDetails,
    setCertificationDetails,
    setwizardPageName,
    setwizardPageNumber,
    setWizardStepValidation,
    setCheckPage,
    setInvites,
    addInvite,
    updateInvite,
    removeInvite,
    clearInvites,
    setKycToken
} = EntityAccountReducer.actions;
export default EntityAccountReducer.reducer;
